<script setup lang="ts">
import { onMounted } from 'vue';
import localforage from 'localforage';
import { useAuth0 } from '@auth0/auth0-vue';
import { useStore as useCompanyStore } from '@/stores/company';
import { useStore as useQueueStore } from '@/stores/queue';
import { logToBetterStack, heapMB } from '@/utils/logger';

// Mounted from App.vue keyed on the Auth0 companyId claim. When the claim
// becomes available (fresh login) or changes (post-switch redirect), this
// component remounts and re-runs the per-company side effects: rehydrating
// the offline reading queue from localforage and refreshing the global
// companies list that drives "Switch Company" menu visibility.
//
// This pattern (key-driven remount) avoids a `watch` while still reacting
// to async auth state changes.

const { user } = useAuth0();
const companyStore = useCompanyStore();
const queueStore = useQueueStore();

onMounted(async () => {
  if (!user.value) return;
  const companyId = user.value['https://app.etainabl.com/companyId'] as
    | string
    | undefined;
  if (!companyId) return;

  // Rehydrate the per-company offline queue. Writes are handled by
  // App.vue's $subscribe; this is the matching read.
  const queue = (await localforage.getItem(`queue-${companyId}`)) as
    | string
    | null;
  if (queue) {
    // Persisted size BEFORE compaction — the key memory signal (pre-fix builds
    // accumulate base64 photos here and rehydrate them all into the heap).
    const persistedBytes = queue.length;
    let parsed = false;
    try {
      queueStore.$patch(JSON.parse(queue));
      parsed = true;
    } catch (e) {
      // Corrupt/truncated persisted JSON (e.g. an abrupt iOS kill mid-write)
      // must not block app boot — drop the bad key and recover with an empty
      // queue rather than throwing during mount.
      await localforage.removeItem(`queue-${companyId}`);
      logToBetterStack('queue_rehydrate_corrupt', { persistedBytes, error: String(e) }, 'error');
    }
    if (parsed) {
      // Compact any historical bloat (base64 photos retained in submitted items
      // by pre-fix builds) immediately on load.
      queueStore.reconcileAfterRehydrate();
      logToBetterStack('queue_rehydrated', {
        persistedBytes,
        persistedMB: +(persistedBytes / 1048576).toFixed(2),
        itemsAfterCompaction: queueStore.queue.length,
        photosRetained: queueStore.queue.filter((i) => i.state.photoData.dataUrl).length,
        heapMB: heapMB()
      });
    }
  }

  // Populate the global companies list. A failure here just means the
  // Switch Company menu item won't appear this session — the
  // SwitchCompanyView's own load() handles user-facing retry if the user
  // navigates there directly.
  companyStore.getGlobalCompanies().catch(() => {});
});
</script>
